import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faChevronLeft,
    faChevronRight,
    faXmark
} from "@fortawesome/free-solid-svg-icons";

import { OMNISCAN_BLUE } from "../constants/colors";


export default function ImageGallery({
    visible,
    photos = [],
    selectedPhoto,
    onSelectPhoto,
    onClose
}) {

    const [currentPhoto, setCurrentPhoto] =
        useState(selectedPhoto);

    const [fade, setFade] =
        useState(true);


    //----------------------------------------------------
    // Synchronise selected photograph
    //----------------------------------------------------

    useEffect(() => {

        setCurrentPhoto(selectedPhoto);

    }, [selectedPhoto]);


    //----------------------------------------------------
    // Escape key closes gallery
    //----------------------------------------------------

    useEffect(() => {

        if (!visible) {
            return;
        }

        const handleKeyDown = (event) => {

            if (event.key === "Escape") {
                onClose?.();
            }

        };

        document.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {

            document.removeEventListener(
                "keydown",
                handleKeyDown
            );

        };

    }, [visible, onClose]);


    //----------------------------------------------------
    // Current photograph index
    //----------------------------------------------------

    const currentIndex =
        photos.findIndex(
            p =>
                currentPhoto &&
                p.sharePointId === currentPhoto.sharePointId
        );


    //----------------------------------------------------
    // Change photograph
    //----------------------------------------------------

    const changePhoto = (photo) => {

        if (!photo) {
            return;
        }

        setFade(false);

        setTimeout(() => {

            setCurrentPhoto(photo);

            if (onSelectPhoto) {
                onSelectPhoto(photo);
            }

            setFade(true);

        }, 150);

    };


    //----------------------------------------------------
    // Previous photograph
    //----------------------------------------------------

    const previous = () => {

        if (currentIndex <= 0) {
            return;
        }

        changePhoto(
            photos[currentIndex - 1]
        );

    };


    //----------------------------------------------------
    // Next photograph
    //----------------------------------------------------

    const next = () => {

        if (
            currentIndex < 0 ||
            currentIndex >= photos.length - 1
        ) {
            return;
        }

        changePhoto(
            photos[currentIndex + 1]
        );

    };


    //----------------------------------------------------
    // Hidden
    //----------------------------------------------------

    if (!visible) {
        return null;
    }


    //----------------------------------------------------
    // Render directly beneath document.body
    //
    // This prevents parent backdrop filters,
    // transforms and overflow rules from affecting
    // the gallery's fixed positioning.
    //----------------------------------------------------

    return createPortal(

        <>

            {/* ========================================
                BACKDROP
            ======================================== */}

            <div

                onClick={onClose}

                style={{

                    position: "fixed",

                    inset: 0,

                    background:
                        "rgba(3, 9, 16, 0.58)",

                    backdropFilter:
                        "blur(6px)",

                    WebkitBackdropFilter:
                        "blur(6px)",

                    zIndex: 9998

                }}

            />


            {/* ========================================
                GALLERY WINDOW
            ======================================== */}

            <div

                role="dialog"

                aria-modal="true"

                aria-label="Photo Gallery"

                style={{

                    position: "fixed",

                    left: "50%",

                    top: "50%",

                    transform:
                        "translate(-50%, -50%)",

                    width:
                        "min(1200px, calc(100vw - 80px))",

                    height:
                        "min(780px, calc(100vh - 80px))",

                    boxSizing: "border-box",

                    display: "flex",

                    flexDirection: "column",

                    minWidth: 0,

                    //------------------------------------------------
                    // Architectural smoked glass
                    //------------------------------------------------

                    background:
                        "linear-gradient(" +
                        "145deg, " +
                        "rgba(18,28,38,0.94) 0%, " +
                        "rgba(10,19,29,0.92) 52%, " +
                        "rgba(5,12,20,0.96) 100%" +
                        ")",

                    backdropFilter:
                        "blur(12px)",

                    WebkitBackdropFilter:
                        "blur(12px)",

                    //------------------------------------------------
                    // Glass border
                    //------------------------------------------------

                    border:
                        "1px solid rgba(141,200,240,0.24)",

                    borderRadius: "18px",

                    //------------------------------------------------
                    // Depth
                    //------------------------------------------------

                    boxShadow:
                        "0 30px 80px rgba(0,0,0,0.50), " +
                        "inset 0 1px 0 rgba(255,255,255,0.06)",

                    overflow: "hidden",

                    zIndex: 9999

                }}

            >


                {/* ====================================
                    HEADER
                ==================================== */}

                <div

                    style={{

                        height: "62px",

                        flexShrink: 0,

                        display: "flex",

                        alignItems: "center",

                        justifyContent:
                            "space-between",

                        padding: "0 24px",

                        boxSizing: "border-box",

                        color: "#ffffff",

                        fontWeight: 700,

                        fontSize: "17px",

                        background:
                            "rgba(3,9,16,0.30)",

                        borderBottom:
                            "1px solid rgba(141,200,240,0.15)"

                    }}

                >

                    <div>
                        Photo Gallery
                    </div>

                    <button

                        type="button"

                        aria-label="Close gallery"

                        onClick={onClose}

                        style={{

                            background: "transparent",

                            border: "none",

                            color: "#ffffff",

                            cursor: "pointer",

                            fontSize: "20px"

                        }}

                    >

                        <FontAwesomeIcon
                            icon={faXmark}
                        />

                    </button>

                </div>


                {/* ====================================
                    MAIN PHOTOGRAPH
                ==================================== */}

                <div

                    style={{

                        flex: "1 1 0",

                        minHeight: 0,

                        minWidth: 0,

                        background:
                            "rgba(3,9,16,0.50)",

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",

                        position: "relative",

                        overflow: "hidden"

                    }}

                >


                    {/* Previous */}

                    <button

                        type="button"

                        aria-label="Previous photograph"

                        onClick={previous}

                        disabled={currentIndex <= 0}

                        style={{

                            position: "absolute",

                            left: "24px",

                            top: "50%",

                            transform:
                                "translateY(-50%)",

                            zIndex: 2,

                            background:
                                "rgba(3,9,16,0.65)",

                            border:
                                "1px solid rgba(255,255,255,0.12)",

                            borderRadius: "50%",

                            width: "44px",

                            height: "44px",

                            color: "#ffffff",

                            cursor:
                                currentIndex > 0
                                    ? "pointer"
                                    : "default",

                            opacity:
                                currentIndex > 0
                                    ? 1
                                    : 0.3

                        }}

                    >

                        <FontAwesomeIcon
                            icon={faChevronLeft}
                        />

                    </button>


                    {/* Photograph */}

                    {
                        currentPhoto &&

                        <img

                            src={currentPhoto.url}

                            alt="Selected gallery photograph"

                            style={{

                                display: "block",

                                maxWidth:
                                    "calc(100% - 140px)",

                                maxHeight: "94%",

                                width: "auto",

                                height: "auto",

                                objectFit: "contain",

                                opacity:
                                    fade ? 1 : 0,

                                transition:
                                    "opacity 0.15s ease-in-out"

                            }}

                        />

                    }


                    {/* Next */}

                    <button

                        type="button"

                        aria-label="Next photograph"

                        onClick={next}

                        disabled={
                            currentIndex < 0 ||
                            currentIndex >= photos.length - 1
                        }

                        style={{

                            position: "absolute",

                            right: "24px",

                            top: "50%",

                            transform:
                                "translateY(-50%)",

                            zIndex: 2,

                            background:
                                "rgba(3,9,16,0.65)",

                            border:
                                "1px solid rgba(255,255,255,0.12)",

                            borderRadius: "50%",

                            width: "44px",

                            height: "44px",

                            color: "#ffffff",

                            cursor:
                                currentIndex >= 0 &&
                                currentIndex < photos.length - 1
                                    ? "pointer"
                                    : "default",

                            opacity:
                                currentIndex >= 0 &&
                                currentIndex < photos.length - 1
                                    ? 1
                                    : 0.3

                        }}

                    >

                        <FontAwesomeIcon
                            icon={faChevronRight}
                        />

                    </button>

                </div>


                {/* ====================================
                    THUMBNAILS
                ==================================== */}

                <div

                    style={{

                        height: "118px",

                        flexShrink: 0,

                        minWidth: 0,

                        display: "flex",

                        alignItems: "center",

                        gap: "14px",

                        overflowX: "auto",

                        overflowY: "hidden",

                        padding: "14px 20px",

                        boxSizing: "border-box",

                        background:
                            "rgba(5,12,20,0.68)",

                        borderTop:
                            "1px solid rgba(141,200,240,0.14)"

                    }}

                >

                    {
                        photos.map((photo, index) =>

                            <img

                                key={
                                    photo.sharePointId || index
                                }

                                src={photo.url}

                                alt={
                                    `Gallery photograph ${index + 1}`
                                }

                                onClick={() =>
                                    changePhoto(photo)
                                }

                                style={{

                                    width: "110px",

                                    height: "78px",

                                    flexShrink: 0,

                                    objectFit: "cover",

                                    borderRadius: "8px",

                                    cursor: "pointer",

                                    boxSizing: "border-box",

                                    border:

                                        currentPhoto &&
                                        currentPhoto.sharePointId ===
                                            photo.sharePointId

                                            ?

                                            "3px solid " +
                                            OMNISCAN_BLUE

                                            :

                                            "3px solid transparent",

                                    transition:
                                        "border-color 0.2s ease"

                                }}

                            />

                        )
                    }

                </div>

            </div>

        </>,

        document.body

    );

}